import { useId, useMemo, useRef, useState } from 'react';
import {
  CONVERSATION_LOCATION_REFERENCES_MAX,
  CONVERSATION_MESSAGE_MAX,
  placeToken,
  requirementToken,
  type ConversationLocations,
} from '@scoutaround/script-breakdown-shared';

import type { ProjectPerson } from '../../lib/api.js';
import { locationMetaLine } from '../LocationCard.js';
import { shortPlaceName } from './LocationPreview.js';

export interface Mentionable {
  readonly userId: number;
  readonly displayName: string;
}

/** `@Name` as typed → `<@id>` as stored, for the people picked from the list. */
export function encodeMentions(text: string, picked: readonly Mentionable[]): string {
  let out = text;
  // Longest names first, so "Ana Maria" is not eaten by "Ana".
  for (const person of [...picked].sort((a, b) => b.displayName.length - a.displayName.length)) {
    out = out.split(`@${person.displayName}`).join(`<@${String(person.userId)}>`);
  }
  return out;
}

export function mentionablesFrom(
  people: readonly ProjectPerson[] | undefined,
  eligible: (person: ProjectPerson) => boolean,
  me: number | null,
): Mentionable[] {
  return (people ?? [])
    .filter((p) => p.memberStatus === 'ACTIVE' && p.userId !== me && p.displayName !== null && eligible(p))
    .map((p) => ({ userId: p.userId, displayName: p.displayName ?? 'Member' }));
}

/** A place or a script location the writer may point at. */
export interface Referenceable {
  /** `place:123` or `requirement:01J…` — stable across renders. */
  readonly key: string;
  readonly group: 'SCRIPT_LOCATION' | 'PLACE';
  /** What `#` is followed by in the text box; unique within one list. */
  readonly display: string;
  readonly title: string;
  readonly detail: string;
  readonly imageUrl: string | null;
  /** The stored form, `<#place:123>` / `<#requirement:…>`. */
  readonly token: string;
}

/**
 * What the "# Location" picker offers: the production's live script locations,
 * then the places it is considering (a rejected one included — it is still a
 * place somebody may want to talk about). A place no longer in the production
 * and a retired script location are not offered; the server would refuse them.
 */
export function referenceablesFrom(locations: ConversationLocations | undefined): Referenceable[] {
  const out: Referenceable[] = [];
  const used = new Map<string, number>();
  const unique = (name: string) => {
    const seen = (used.get(name) ?? 0) + 1;
    used.set(name, seen);
    return seen === 1 ? name : `${name} ${String(seen)}`;
  };

  for (const s of locations?.scriptLocations ?? []) {
    if (!s.live) continue;
    out.push({
      key: `requirement:${s.requirementId}`,
      group: 'SCRIPT_LOCATION',
      display: unique(s.title.trim()),
      title: s.title,
      detail: `${String(s.sceneCount)} ${s.sceneCount === 1 ? 'scene' : 'scenes'} · ${String(s.placeCount)} ${s.placeCount === 1 ? 'place' : 'places'} found`,
      imageUrl: null,
      token: requirementToken(s.requirementId),
    });
  }
  for (const p of locations?.places ?? []) {
    if (!p.inProduction) continue;
    out.push({
      key: `place:${String(p.locationId)}`,
      group: 'PLACE',
      // Without the chip's ellipsis: this is text somebody is writing.
      display: unique(shortPlaceName(p.title).replace(/…$/, '')),
      title: p.title,
      detail: p.kind === 'POI' ? 'A scout’s find' : locationMetaLine(p),
      imageUrl: p.imageUrl,
      token: placeToken(p.locationId),
    });
  }
  return out;
}

/** `#Name` as typed → its token as stored, for the locations picked from the list. */
export function encodeReferences(text: string, picked: readonly Referenceable[]): string {
  let out = text;
  for (const ref of [...picked].sort((a, b) => b.display.length - a.display.length)) {
    out = out.split(`#${ref.display}`).join(ref.token);
  }
  return out;
}

type Picker = 'person' | 'location' | null;

/**
 * Writing a message. `@` opens the list of people this conversation may
 * mention — on a private one only its participants (spec §2) — and `#` the
 * production's script locations and places. Picking one writes `@Name` or
 * `#Name`; the stored form is a token (`<@id>`, `<#place:id>`), so a renamed
 * account or listing reads correctly later and a place is always shown as the
 * reader may see it. Cmd/Ctrl+Enter sends.
 */
export function Composer({
  mentionable,
  referenceable = [],
  disabled = false,
  disabledReason = null,
  submitLabel = 'Send',
  placeholder = 'Write a message',
  testId,
  onSend,
}: {
  mentionable: readonly Mentionable[];
  referenceable?: readonly Referenceable[];
  disabled?: boolean;
  disabledReason?: string | null;
  submitLabel?: string;
  placeholder?: string;
  testId: string;
  onSend: (body: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<Mentionable[]>([]);
  const [pickedRefs, setPickedRefs] = useState<Referenceable[]>([]);
  const [picking, setPicking] = useState<Picker>(null);
  const [filter, setFilter] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const listId = useId();

  const needle = filter.toLowerCase();
  const people = useMemo(
    () => mentionable.filter((m) => m.displayName.toLowerCase().includes(needle)),
    [mentionable, needle],
  );
  const locations = useMemo(
    () =>
      referenceable.filter(
        (r) => r.title.toLowerCase().includes(needle) || r.detail.toLowerCase().includes(needle),
      ),
    [referenceable, needle],
  );

  const open = (which: Picker) => {
    setFilter('');
    setPicking(which);
  };

  /** Replaces the `@`/`#` just typed (if any) with the picked name and a space. */
  const insertAtCursor = (sigil: '@' | '#', name: string) => {
    const el = area.current;
    const at = el?.selectionStart ?? text.length;
    const before = text.slice(0, at);
    const trimmed = before.endsWith(sigil) ? before.slice(0, -1) : before;
    setText(`${trimmed}${sigil}${name} ${text.slice(at)}`);
    setPicking(null);
    setFilter('');
    requestAnimationFrame(() => el?.focus());
  };

  const insertPerson = (person: Mentionable) => {
    insertAtCursor('@', person.displayName);
    setPicked((current) => (current.some((p) => p.userId === person.userId) ? current : [...current, person]));
  };

  const insertLocation = (ref: Referenceable) => {
    insertAtCursor('#', ref.display);
    setPickedRefs((current) => (current.some((r) => r.key === ref.key) ? current : [...current, ref]));
  };

  const send = async () => {
    const body = encodeReferences(encodeMentions(text.trim(), picked), pickedRefs);
    if (body === '' || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(body);
      setText('');
      setPicked([]);
      setPickedRefs([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The message could not be sent. Try again.');
    } finally {
      setSending(false);
    }
  };

  if (disabled) {
    return (
      <p className="mm" data-testid={`${testId}-disabled`} style={{ margin: 0, fontSize: 13 }}>
        {disabledReason ?? 'You cannot write here.'}
      </p>
    );
  }

  const pickerStyle = {
    border: '1px solid var(--color-neutral-300)',
    background: 'var(--color-bg)',
    padding: 8,
    display: 'grid',
    gap: 6,
  } as const;
  const optionStyle = { width: '100%', textAlign: 'left', border: 'none', minHeight: 32 } as const;
  const groupLabel = { margin: '4px 0 2px', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' } as const;
  const groups: readonly (readonly [string, readonly Referenceable[]])[] = [
    ['Script locations', locations.filter((r) => r.group === 'SCRIPT_LOCATION')],
    ['Places', locations.filter((r) => r.group === 'PLACE')],
  ];

  return (
    <form
      data-testid={testId}
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
      style={{ display: 'grid', gap: 8, position: 'relative' }}
    >
      <textarea
        ref={area}
        data-testid={`${testId}-input`}
        aria-label="Message"
        value={text}
        maxLength={CONVERSATION_MESSAGE_MAX}
        rows={3}
        placeholder={placeholder}
        onChange={(event) => {
          const value = event.target.value;
          setText(value);
          if (value.endsWith('@') && mentionable.length > 0) open('person');
          if (value.endsWith('#') && referenceable.length > 0) open('location');
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void send();
          }
          if (event.key === 'Escape' && picking !== null) {
            event.stopPropagation();
            setPicking(null);
          }
        }}
        style={{ width: '100%', padding: 10, border: '1px solid var(--color-neutral-300)', fontSize: 14, resize: 'vertical' }}
      />
      {picking === 'person' ? (
        <div data-testid={`${testId}-mention-picker`} style={pickerStyle}>
          <input
            aria-label="Find a person to mention"
            aria-controls={listId}
            data-testid={`${testId}-mention-filter`}
            value={filter}
            autoFocus
            onChange={(event) => {
              setFilter(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                setPicking(null);
              }
            }}
            placeholder="Find a person"
            style={{ padding: 6, border: '1px solid var(--color-neutral-300)' }}
          />
          <ul id={listId} style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 180, overflowY: 'auto' }}>
            {people.length === 0 ? (
              <li className="mm" style={{ fontSize: 12.5 }}>Nobody to mention here.</li>
            ) : (
              people.map((person) => (
                <li key={person.userId}>
                  <button
                    type="button"
                    className="ctl"
                    data-testid={`${testId}-mention-option-${String(person.userId)}`}
                    onClick={() => {
                      insertPerson(person);
                    }}
                    style={optionStyle}
                  >
                    {person.displayName}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
      {picking === 'location' ? (
        <div data-testid={`${testId}-location-picker`} style={pickerStyle}>
          <input
            aria-label="Find a location to mention"
            aria-controls={listId}
            data-testid={`${testId}-location-filter`}
            value={filter}
            autoFocus
            onChange={(event) => {
              setFilter(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                setPicking(null);
              }
            }}
            placeholder="Find a script location or a place"
            style={{ padding: 6, border: '1px solid var(--color-neutral-300)' }}
          />
          <div id={listId} style={{ maxHeight: 260, overflowY: 'auto' }}>
            {locations.length === 0 ? (
              <p className="mm" style={{ margin: 0, fontSize: 12.5 }}>
                {referenceable.length === 0 ? 'No locations in this production yet.' : 'Nothing matches that.'}
              </p>
            ) : null}
            {groups.map(([label, group]) =>
              group.length === 0 ? null : (
                <section key={label} aria-label={label}>
                  <p className="mm" style={groupLabel}>{label}</p>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {group.map((ref) => (
                      <li key={ref.key}>
                        <button
                          type="button"
                          className="ctl"
                          data-testid={`${testId}-location-option-${ref.key}`}
                          onClick={() => {
                            insertLocation(ref);
                          }}
                          style={{ ...optionStyle, display: 'flex', gap: 10, alignItems: 'center', padding: '4px 6px' }}
                        >
                          {ref.group === 'PLACE' ? (
                            ref.imageUrl ? (
                              <img
                                src={ref.imageUrl}
                                alt=""
                                loading="lazy"
                                style={{ width: 44, height: 32, objectFit: 'cover', flex: 'none', background: 'var(--color-neutral-100)' }}
                              />
                            ) : (
                              <span aria-hidden="true" style={{ width: 44, height: 32, flex: 'none', background: 'var(--color-neutral-100)' }} />
                            )
                          ) : null}
                          <span style={{ display: 'grid', minWidth: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ref.title}
                            </span>
                            {ref.detail !== '' ? (
                              <span className="mm" style={{ fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ref.detail}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ),
            )}
          </div>
        </div>
      ) : null}
      {error ? (
        <p role="alert" data-testid={`${testId}-error`} style={{ margin: 0, color: 'var(--danger)', fontSize: 13 }}>
          {error}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="ctl"
            data-testid={`${testId}-mention`}
            disabled={mentionable.length === 0}
            onClick={() => {
              open(picking === 'person' ? null : 'person');
            }}
            aria-expanded={picking === 'person'}
            title={mentionable.length === 0 ? 'Nobody else can be mentioned here' : 'Mention someone'}
          >
            @ Mention
          </button>
          <button
            type="button"
            className="ctl"
            data-testid={`${testId}-location`}
            disabled={referenceable.length === 0}
            onClick={() => {
              open(picking === 'location' ? null : 'location');
            }}
            aria-expanded={picking === 'location'}
            title={
              referenceable.length === 0
                ? 'This production has no script locations or places yet'
                : `Point at a script location or a place (up to ${String(CONVERSATION_LOCATION_REFERENCES_MAX)} a message)`
            }
          >
            # Location
          </button>
        </div>
        <button
          type="submit"
          className="ctl"
          data-on="1"
          data-testid={`${testId}-send`}
          disabled={sending || text.trim() === ''}
        >
          {sending ? 'Sending…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
